import React, { useContext, useEffect } from 'react';
import FormGroup from '@mui/material/FormGroup';
import { useTranslation } from 'react-i18next';
import { ApolloError } from '@apollo/client';
import Grid from '@mui/material/Grid';
import { makeStyles } from '@mui/styles';
import { Theme } from '@mui/material/styles';
import CircularProgress from '@mui/material/CircularProgress';
import Switch from '@mui/material/Switch';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import FormControlLabel from '@mui/material/FormControlLabel';
import Button from '@mui/material/Button';
import validator from 'validator';
import QRCode from 'qrcode.react';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import TextField from '@mui/material/TextField';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import LinkIcon from '@mui/icons-material/Link';
import ContactMailIcon from '@mui/icons-material/ContactMail';
import { parseDomain, ParseResultType } from 'parse-domain';
import useForm from '../../../components/_hook/useForm';
import useLanguage from '../../../components/_hook/useLanguage';
import InputTranslationTextField from '../../../components/_input/InputTranslation/InputTranslationTextField';
import { useUpdateCodeMutation } from '../../../../graphql/mutation/code/updateCodeMutation';
import useNotification from '../../../components/_hook/useNotification';
import useRouter from '../../../components/_hook/useRouter';
import { useCodeQuery } from '../../../../graphql/query/code/useCodeQuery';
import NotFound from '../../../components/_error/NotFound';
import useRoutes from '../../../components/_hook/useRoutes';
import { ITranslationSubTypeInput } from '../../../../graphql/type/_inputTypes/translation';
import Select, {
  IValue,
  IValues,
} from '../../../components/_select/_module/Select';
import { useMinisiteQuery } from '../../../../graphql/query/minisite/useMinisiteQuery';
import address from '../../../components/util/address';
import InputTranslationTextarea from '../../../components/_input/InputTranslation/InputTranslationTextarea';
import { useCreateCodeMutation } from '../../../../graphql/mutation/code/createCodeMutation';
import { useCompanyQuery } from '../../../../graphql/query/company/useCompanyQuery';
import CodeLayout from '../../../layout/Code';
import Context from '../../../context';
import random from '../../../components/util/random';
import StickyFormFooter from '../../../components/_form/StickyFormFooter';

const useStyles = makeStyles<Theme>((theme: Theme) => ({
  paper: {
    margin: theme.spacing(1),
    padding: theme.spacing(3),
  },
  sourceUrlPaper: {
    width: '100%',
    margin: theme.spacing(1),
    padding: theme.spacing(1),
  },
  contentCard: {
    width: '100%',
    backgroundColor: theme.palette.mode === 'light' ? '#f3f3f3' : 'inherit',
  },
  tab: {
    backgroundColor: theme.palette.mode === 'light' ? '#fff' : '#000',
  },
}));

function CodeDetail() {
  const companyFragment = `
  _id
            name {
                la
                text
            }
            origin
            slug
            contacts {
              emails {
                id
                title {
                  la
                  text
                }
                status
                address
              }
              websites {
                id
                title {
                  la
                  text
                }
                status
                address
              }
            }
            branches {
              tel {
                id
                title {
                  la
                  text
                }
                status
                countryCode
                number
              }
              address {
                  id
                  streetNo
                  streetName
                  unitNo
                  buildingName
                  town
                  district
                  city
                  state
                  country
                  countryData {
                    _id
                    name {
                      la
                      text
                    }
                  }
                  zip
              }
            }
            teams {
                id
                userData {
                  _id
                  profile {
                    name {
                      firstname
                      lastname
                    }
                    works {
                      id
                      contact {
                        tel {
                          id
                          title {
                            la
                            text
                          }
                          status
                          countryCode
                          number
                        }
                        emails {
                          id
                          title {
                            la
                            text
                          }
                          status
                          address
                        }
                      }
                    }
                  }
                }
                status
                departments
            }
  `;

  interface IFormValue {
    label: ITranslationSubTypeInput[];
    description: ITranslationSubTypeInput[];
    status: boolean;
    company: string;

    codeType: string;
    code: string;
    codeDomainName: string;
    codeDestinationType: string;
    codeDestinationUrl: string;
    codeDestinationUser: string;
    codeDestinationCompanyAddress?: string;
    codeDestinationPhones: string[];
    codeDestinationEmails: string[];
    codeDestinationIncludeProfileLink: boolean;
    codeDestinationCompanyWebsite: string;
  }

  const { t } = useTranslation();
  const classes = useStyles();
  const { notification } = useNotification();
  const { params, history } = useRouter();
  const { path } = useRoutes();
  const { docID: codeId } = params;
  const { company } = useContext(Context);
  const { getText } = useLanguage();

  const { data, loading } = useCodeQuery(
    `
			_id
            status
            company
            companyData {
              ${companyFragment}
            }
            label {
                la
                text
            }
            description {
                la
                text
            }
            code_type
            code_details {
              code
              domain_name
              destination_type
              destination_details {
                url
                user
                companyAddress
                phones 
                emails
                include_profile_link
                companyWebsite
              }
            }
		`,
    {
      variables: {
        filter: {
          companyId: company._id,
          _id: codeId as string,
        },
      },
      skip: codeId === 'new',
    }
  );

  const { data: minisiteData, loading: isMinisiteLoading } = useMinisiteQuery(
    `
			_id
            status
            domain
            company
		`,
    {
      variables: {
        filter: {
          companyId: company._id,
        },
      },
    }
  );

  const { data: companyData } = useCompanyQuery(companyFragment, {
    variables: {
      filter: {
        _id: company._id,
      },
    },
    skip: codeId !== 'new',
  });

  function onCompleted() {
    notification.success(t('success'));
    history.push(
      path('code.list', {
        companyID: company._id,
      })
    );
  }

  function onError(e: ApolloError) {
    notification.error(e.message);
  }

  const [
    updateCodeMutation,
    { loading: isUpdatingCode },
  ] = useUpdateCodeMutation(
    `updatedCode {
				_id
            status
            company
            label {
                la
                text
            }
            description {
                la
                text
            }
            code_type
            code_details {
              code
              domain_name
              destination_type
              destination_details {
                url
                user
                companyAddress
                phones 
                emails
                include_profile_link
                companyWebsite
              }
            }
            updated {
                time
            }
			}`,
    {
      onCompleted,
      onError,
    }
  );

  const [
    createCodeMutation,
    { loading: isCreatingCode },
  ] = useCreateCodeMutation(
    `createdCode {
				_id
            status
            company
            label {
                la
                text
            }
            description {
                la
                text
            }
            code_type
            code_details {
              code
              domain_name
              destination_type
              destination_details {
                url
                user
                companyAddress
                phones 
                emails
                include_profile_link
                companyWebsite
              }
            }
            updated {
                time
            }
			}`,
    {
      onCompleted,
      onError,
    }
  );

  async function onSubmitForm(values: IFormValue) {
    if (data) {
      await updateCodeMutation({
        variables: {
          _id: data.codes.items[0]._id,
          label: values.label,
          description: values.description,
          status: values.status,
          company: values.company,
          codeType: values.codeType,
          code: values.code,
          codeDomainName: values.codeDomainName,
          codeDestinationType: values.codeDestinationType,
          codeDestinationUrl: values.codeDestinationUrl,
          codeDestinationUser: values.codeDestinationUser,
          codeDestinationCompanyAddress: values.codeDestinationCompanyAddress,
          codeDestinationPhones: values.codeDestinationPhones,
          codeDestinationEmails: values.codeDestinationEmails,
          codeDestinationIncludeProfileLink:
            values.codeDestinationIncludeProfileLink,
          codeDestinationCompanyWebsite: values.codeDestinationCompanyWebsite,
        },
      });
    } else {
      await createCodeMutation({
        variables: {
          label: values.label,
          description: values.description,
          status: values.status,
          company: values.company,
          codeType: values.codeType,
          code: values.code,
          codeDomainName: values.codeDomainName,
          codeDestinationType: values.codeDestinationType,
          codeDestinationUrl: values.codeDestinationUrl,
          codeDestinationUser: values.codeDestinationUser,
          codeDestinationCompanyAddress: values.codeDestinationCompanyAddress,
          codeDestinationPhones: values.codeDestinationPhones,
          codeDestinationEmails: values.codeDestinationEmails,
          codeDestinationIncludeProfileLink:
            values.codeDestinationIncludeProfileLink,
          codeDestinationCompanyWebsite: values.codeDestinationCompanyWebsite,
        },
      });
    }
  }

  const {
    values,
    setValue,
    handleSubmit,
    errors,
    setValues,
  } = useForm<IFormValue>({
    defaultValues: {
      label: [],
      description: [],
      status: false,
      company: company._id || '',
      codeType: 'qr_code',
      code: random.id(17),
      codeDomainName: '',
      codeDestinationType: 'link',
      codeDestinationUrl: '',
      codeDestinationUser: '',
      codeDestinationCompanyAddress: '',
      codeDestinationPhones: [],
      codeDestinationEmails: [],
      codeDestinationIncludeProfileLink: false,
      codeDestinationCompanyWebsite: '',
    },
    onSubmit: onSubmitForm,
    onValidate: (tempValues) => {
      const newErrors: Record<string, string> = {};

      if (!tempValues.label.length || !getText(tempValues.label)) {
        newErrors.label = t('please_enter_the_correct__type__', {
          type: t('label'),
        });
      }

      if (tempValues.codeType === 'qr_code') {
        if (!tempValues.codeDomainName) {
          newErrors.codeDomainName = t('__type__is_required', {
            type: t('domain_name'),
          });
        }
        if (!tempValues.code) {
          newErrors.code = t('__type__is_required', {
            type: t('code'),
          });
        } else if (!validator.isAlphanumeric(tempValues.code)) {
          newErrors.codeDomainName = t('__name__is_invalid', {
            name: `${t('code')}`,
          });
        }

        if (tempValues.codeDestinationType === 'link') {
          if (tempValues.codeDestinationUrl === '') {
            newErrors.codeDestinationUrl = t('__type__is_required', {
              type: `${t('destination')} URL`,
            });
          } else if (!validator.isURL(tempValues.codeDestinationUrl)) {
            newErrors.codeDestinationUrl = t('invalid__type__url', {
              type: t('destination'),
            });
          }
        } else if (tempValues.codeDestinationType === 'contact') {
          if (!tempValues.codeDestinationUser) {
            newErrors.codeDestinationUser = t('__type__is_required', {
              type: t('user'),
            });
          }
        }
      }

      Object.keys(newErrors).forEach((newError) =>
        notification.error(newErrors[newError])
      );

      return newErrors;
    },
  });

  useEffect(() => {
    if (!loading && data && data.codes.items.length) {
      const code = data.codes.items[0];
      const codeDetails = code.code_details;
      const codeDestinationDetails = codeDetails.destination_details;

      setValues({
        label: code.label.map((label) => ({
          text: label.text,
          la: label.la,
        })),
        description: code.description.map((description) => ({
          text: description.text,
          la: description.la,
        })),
        status: code.status,
        company: code.company || '',
        codeType: code.code_type,
        code: codeDetails.code,
        codeDomainName: codeDetails.domain_name,
        codeDestinationType: codeDetails.destination_type,
        codeDestinationUrl: codeDestinationDetails.url || '',
        codeDestinationUser: codeDestinationDetails.user || '',
        codeDestinationCompanyAddress:
          codeDestinationDetails.companyAddress || '',
        codeDestinationPhones: codeDestinationDetails.phones || [],
        codeDestinationEmails: codeDestinationDetails.emails || [],
        codeDestinationIncludeProfileLink:
          codeDestinationDetails.include_profile_link || false,
        codeDestinationCompanyWebsite:
          codeDestinationDetails.companyWebsite || '',
      });
    }
  }, [loading]);

  const codeDestinationTypes = [
    {
      name: 'link',
      comingSoon: false,
      icon: <LinkIcon />,
    },
    {
      name: 'contact',
      comingSoon: false,
      icon: <ContactMailIcon />,
    },
    // {
    //   name: 'call',
    //   comingSoon: true,
    //   icon: <CallIcon/>
    // },
    // {
    //   name: 'message',
    //   comingSoon: true,
    //   icon: <MessageIcon/>
    // },
    // {
    //   name: 'email',
    //   comingSoon: true,
    //   icon: <EmailIcon/>
    // },
    // {
    //   name: 'more',
    //   comingSoon: true,
    //   icon: <MoreHorizIcon/>
    // }
  ];

  const domains =
    minisiteData && !isMinisiteLoading
      ? minisiteData.minisites.items.map((minisite) => minisite.domain).flat()
      : [];

  if (codeId !== 'new' && loading && codeId) {
    return (
      <Grid container justifyContent="center">
        <CircularProgress size={30} color="inherit" />
      </Grid>
    );
  }

  const code = codeId && data ? data.codes.items[0] : null;
  const keepitalCompany = companyData ? companyData.companies.items[0] : null;

  if (!code && codeId !== 'new') {
    return <NotFound />;
  }

  let codeCompany = code && code.companyData ? code.companyData : null;

  if (keepitalCompany && codeId === 'new') {
    codeCompany = keepitalCompany;
  }

  const teams =
    codeCompany && codeCompany.teams
      ? codeCompany.teams
          .filter((team) => team.userData)
          .map((team) => team.userData)
      : [];

  const companyAddresses =
    codeCompany && codeCompany.branches
      ? codeCompany.branches.map((branch) => ({
          label: address.format(branch.address),
          value: branch.address.id,
        }))
      : [];

  const selectedUser = values.codeDestinationUser
    ? teams.find((team) => team?._id === values.codeDestinationUser)
    : null;
  const selectedUserPhones: IValues = [];
  const companyPhones: { label: string; options: IValues }[] = [];
  const selectedUserEmails: IValues = [];
  const companyEmails: IValues = [];
  const companyWebsites: IValues = [];

  if (selectedUser && selectedUser.profile && codeCompany) {
    const userWorkProfiles = selectedUser.profile.works || [];
    const userWorkProfile = userWorkProfiles.find(
      (profile) => profile.id === codeCompany?._id
    );
    // corporate tels
    if (
      userWorkProfile &&
      userWorkProfile.contact &&
      userWorkProfile.contact.tel
    ) {
      userWorkProfile.contact.tel
        .filter((tel) => tel.status && tel.number)
        .forEach((tel) => {
          const telNumber = `+${tel.countryCode} ${tel.number}`;

          selectedUserPhones.push({
            label: getText(tel.title)
              ? `${getText(tel.title)} (${telNumber})`
              : telNumber,
            value: tel.id,
          });
        });
    }
    if (
      userWorkProfile &&
      userWorkProfile.contact &&
      userWorkProfile.contact.emails
    ) {
      userWorkProfile.contact.emails
        .filter((email) => email.status && email.address)
        .forEach((email) => {
          selectedUserEmails.push({
            label:
              email.title && getText(email.title)
                ? `${getText(email.title)} (${email.address})`
                : email.address,
            value: email.id as string,
          });
        });
    }
  }

  if (codeCompany && codeCompany.branches) {
    codeCompany.branches.forEach((branch) => {
      const branchPhones: IValues = [];
      const branchTel = branch.tel || [];
      branchTel
        .filter((tel) => tel.status && tel.number)
        .forEach((tel) => {
          const telNumber = `+${tel.countryCode} ${tel.number}`;

          branchPhones.push({
            label: getText(tel.title)
              ? `${getText(tel.title)} (${telNumber})`
              : telNumber,
            value: tel.id,
          });
        });
      companyPhones.push({
        label: t('company__type__in__location__', {
          type: t('tel', { count: branchPhones.length }),
          location: address.format(branch.address),
        }),
        options: branchPhones,
      });
    });
  }

  if (codeCompany && codeCompany.contacts) {
    if (codeCompany.contacts.emails) {
      codeCompany.contacts.emails
        .filter((email) => email.status && email.address)
        .forEach((email) => {
          companyEmails.push({
            label:
              email.title && getText(email.title)
                ? `${getText(email.title)} (${email.address})`
                : email.address,
            value: email.id as string,
          });
        });
    }
    if (codeCompany.contacts.websites) {
      codeCompany.contacts.websites
        .filter((website) => website.status && website.address)
        .forEach((website) => {
          companyWebsites.push({
            label:
              website.title && getText(website.title)
                ? `${getText(website.title)} (${website.address})`
                : website.address,
            value: website.id,
          });
        });
    }
  }

  const domainData = parseDomain(values.codeDomainName);

  return (
    <Paper square className={classes.paper}>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Typography variant="h6">
            {code ? (
              <>
                {t('update')} {getText(code.label)} {t('code')}
              </>
            ) : (
              <>
                {t('create')} {t('code')}
              </>
            )}
          </Typography>
        </Grid>
        <Grid container item xs={12} spacing={1} justifyContent="center">
          <Grid container item xs={12} justifyContent="center">
            <QRCode
              id="codeCanvas"
              value={`${
                domainData.type === ParseResultType.Listed &&
                domainData &&
                domainData.subDomains &&
                domainData.subDomains.length === 0
                  ? 'http://www.'
                  : 'http://'
              }${values.codeDomainName}/code/${values.code}`}
              size={256}
            />
            <div style={{ display: 'none' }}>
              <QRCode
                id="codeSvg"
                value={`${
                  domainData.type === ParseResultType.Listed &&
                  domainData &&
                  domainData.subDomains &&
                  domainData.subDomains.length === 0
                    ? 'http://www.'
                    : 'http://'
                }${values.codeDomainName}/code/${values.code}`}
                renderAs="svg"
                size={256}
              />
            </div>
          </Grid>
          <Grid container item xs={12} spacing={1} justifyContent="center">
            <Grid item>
              <Button
                variant="contained"
                onClick={() => {
                  const codeFileName = getText(values.label);
                  if (!codeFileName) {
                    notification.error(t('please_fill_in_label_code_first'));
                  } else {
                    const canvas = document.getElementById(
                      'codeCanvas'
                    ) as HTMLCanvasElement;
                    if (canvas) {
                      const link = document.createElement('a');
                      link.download = `${codeFileName}_code.png`;
                      link.href = canvas.toDataURL('image/png', 1);
                      link.click();
                    }
                  }
                }}
                color="primary"
              >
                {t('download_image')}
              </Button>
            </Grid>
            <Grid item>
              <Button
                variant="contained"
                onClick={() => {
                  const codeFileName = getText(values.label);
                  if (!codeFileName) {
                    notification.error(t('please_fill_in_label_code_first'));
                  } else {
                    const svg = document.getElementById('codeSvg');

                    if (svg) {
                      const serializer = new XMLSerializer();
                      let source = serializer.serializeToString(svg);

                      if (
                        !source.match(
                          /^<svg[^>]+xmlns="http:\/\/www\.w3\.org\/2000\/svg"/
                        )
                      ) {
                        source = source.replace(
                          /^<svg/,
                          '<svg xmlns="http://www.w3.org/2000/svg"'
                        );
                      }
                      if (
                        !source.match(
                          /^<svg[^>]+"http:\/\/www\.w3\.org\/1999\/xlink"/
                        )
                      ) {
                        source = source.replace(
                          /^<svg/,
                          '<svg xmlns:xlink="http://www.w3.org/1999/xlink"'
                        );
                      }
                      source = `<?xml version="1.0" standalone="no"?>\r\n${source}`;

                      const link = document.createElement('a');
                      link.download = `${codeFileName}_code.svg`;
                      link.href = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(
                        source
                      )}`;
                      link.click();
                    }
                  }
                }}
                color="primary"
              >
                {t('download_svg')}
              </Button>
            </Grid>
          </Grid>
        </Grid>
        <Grid container item xs={12} spacing={3}>
          <Grid item container spacing={2}>
            <Grid item xs={12}>
              <FormGroup row>
                <FormControlLabel
                  control={
                    <Switch
                      checked={values.status}
                      onChange={() => setValue('status', !values.status)}
                      color="primary"
                    />
                  }
                  label={t('publish')}
                />
              </FormGroup>
            </Grid>
            <Grid item xs={12}>
              <InputTranslationTextField
                fullWidth
                required
                label={t('label')}
                values={values.label}
                onChange={(tempValues) => setValue('label', tempValues)}
                error={Boolean(errors.label)}
                helperText={errors.label}
              />
            </Grid>
            <Grid item xs={12}>
              <InputTranslationTextarea
                variant="outlined"
                fullWidth
                required
                label={t('description')}
                values={values.description}
                onChange={(tempValues) => setValue('description', tempValues)}
                error={Boolean(errors.description)}
                helperText={errors.description}
              />
            </Grid>
          </Grid>

          <Grid item xs={12}>
            <Card className={classes.contentCard}>
              <CardContent>
                <Grid container spacing={1}>
                  <Grid item xs={12}>
                    <Typography variant="h6" gutterBottom>
                      {t('source')}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Paper elevation={0} className={classes.sourceUrlPaper}>
                      <Grid container item xs={12} justifyContent="center">
                        <Typography display="inline">
                          {domainData.type === ParseResultType.Listed &&
                          domainData &&
                          domainData.subDomains &&
                          domainData.subDomains.length === 0
                            ? 'http://www.'
                            : 'http://'}
                        </Typography>
                        <Typography color="primary" display="inline">
                          {values.codeDomainName}
                        </Typography>
                        <Typography display="inline">/code/</Typography>
                        <Typography color="primary" display="inline">
                          {values.code}
                        </Typography>
                      </Grid>
                    </Paper>
                  </Grid>
                  {values.codeType !== 'keepital_qr_code' && (
                    <Grid item xs={12}>
                      <Select<IValue>
                        isClearable
                        loading={isMinisiteLoading}
                        options={domains.map((domain) => ({
                          label: domain,
                          value: domain,
                        }))}
                        label={t('domain')}
                        fullWidth
                        placeholder={t('please_select')}
                        menuPosition="fixed"
                        value={values.codeDomainName}
                        onChange={(option) =>
                          setValue('codeDomainName', option ? option.value : '')
                        }
                        error={Boolean(errors.codeDomainName)}
                        helperText={errors.codeDomainName}
                      />
                    </Grid>
                  )}
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      required
                      label={t('code')}
                      value={values.code}
                      onChange={(e) => setValue('code', e.target.value)}
                      error={Boolean(errors.code)}
                      helperText={errors.code}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12}>
            <Card className={classes.contentCard}>
              <CardContent>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Typography variant="h6" gutterBottom>
                      {t('destination')}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="body1" gutterBottom>
                      {t('Type')}
                    </Typography>
                    <Tabs
                      className={classes.tab}
                      value={values.codeDestinationType}
                      onChange={(e, newValue) =>
                        setValue('codeDestinationType', newValue)
                      }
                      indicatorColor="primary"
                      textColor="primary"
                    >
                      {codeDestinationTypes.map((codeDestinationType) => (
                        <Tab
                          disabled={codeDestinationType.comingSoon}
                          key={codeDestinationType.name}
                          label={t(codeDestinationType.name)}
                          value={codeDestinationType.name}
                          icon={codeDestinationType.icon}
                        />
                      ))}
                    </Tabs>
                  </Grid>
                  {values.codeDestinationType === 'link' && (
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        required
                        placeholder="https://www.keepital.com"
                        label={t('address')}
                        value={values.codeDestinationUrl}
                        onChange={(e) =>
                          setValue('codeDestinationUrl', e.target.value)
                        }
                        error={Boolean(errors.codeDestinationUrl)}
                        helperText={errors.codeDestinationUrl}
                      />
                    </Grid>
                  )}
                  {values.codeDestinationType === 'contact' && (
                    <Grid item xs={12} container spacing={2}>
                      <Grid item xs={12}>
                        <Select<IValue>
                          required
                          isClearable
                          options={teams
                            .filter(
                              (team) =>
                                team && team.profile && team.profile.name
                            )
                            .map((team) => ({
                              label: `${team?.profile?.name?.firstname || ''} ${
                                team?.profile?.name?.lastname || ''
                              }`,
                              value: team?._id || '',
                            }))}
                          label={t('employee')}
                          fullWidth
                          placeholder={t('please_select')}
                          menuPosition="fixed"
                          value={values.codeDestinationUser}
                          onChange={(option) => {
                            setValue(
                              'codeDestinationUser',
                              option ? option.value : ''
                            );
                            setValue('codeDestinationCompanyAddress', '');
                            setValue('codeDestinationPhones', []);
                            setValue('codeDestinationEmails', []);
                            setValue(
                              'codeDestinationIncludeProfileLink',
                              false
                            );
                            setValue('codeDestinationCompanyWebsite', '');
                          }}
                          error={Boolean(errors.codeDestinationUser)}
                          helperText={errors.codeDestinationUser}
                        />
                      </Grid>
                      {values.codeDestinationUser && selectedUser && (
                        <>
                          <Grid item xs={12}>
                            <Select<IValue>
                              isClearable
                              options={companyAddresses}
                              label={t('company__type__', {
                                type: t('address'),
                              })}
                              placeholder={t('company__type__', {
                                type: t('address'),
                              })}
                              fullWidth
                              menuPosition="fixed"
                              value={values.codeDestinationCompanyAddress}
                              onChange={(option) =>
                                setValue(
                                  'codeDestinationCompanyAddress',
                                  option ? option.value : ''
                                )
                              }
                              error={Boolean(
                                errors.codeDestinationCompanyAddress
                              )}
                              helperText={errors.codeDestinationCompanyAddress}
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <Select<IValues>
                              isClearable
                              isMulti
                              options={[
                                {
                                  label: t('corporate__type__', {
                                    type: t('tel', {
                                      count: selectedUserPhones.length,
                                    }),
                                  }),
                                  options: selectedUserPhones,
                                },
                                ...companyPhones,
                              ]}
                              label={t('phone')}
                              fullWidth
                              menuPosition="fixed"
                              value={values.codeDestinationPhones}
                              onChange={(options) =>
                                setValue(
                                  'codeDestinationPhones',
                                  options.map((option) => option.value)
                                )
                              }
                              error={Boolean(errors.codeDestinationPhones)}
                              helperText={errors.codeDestinationPhones}
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <Select<IValues>
                              isClearable
                              options={[
                                {
                                  label: t('corporate__type__', {
                                    type: t('email', {
                                      count: selectedUserEmails.length,
                                    }),
                                  }),
                                  options: selectedUserEmails,
                                },
                                {
                                  label: t('corporate__type__', {
                                    type: t('email', {
                                      count: companyEmails.length,
                                    }),
                                  }),
                                  options: companyEmails,
                                },
                              ]}
                              isMulti
                              label={t('email')}
                              fullWidth
                              menuPosition="fixed"
                              value={values.codeDestinationEmails}
                              onChange={(options) =>
                                setValue(
                                  'codeDestinationEmails',
                                  options.map((option) => option.value)
                                )
                              }
                              error={Boolean(errors.codeDestinationEmails)}
                              helperText={errors.codeDestinationEmails}
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <FormGroup row>
                              <FormControlLabel
                                control={
                                  <Switch
                                    checked={
                                      values.codeDestinationIncludeProfileLink
                                    }
                                    onChange={() =>
                                      setValue(
                                        'codeDestinationIncludeProfileLink',
                                        !values.codeDestinationIncludeProfileLink
                                      )
                                    }
                                    color="primary"
                                  />
                                }
                                label={t('include_keepital_profile_link')}
                              />
                            </FormGroup>
                          </Grid>
                          <Grid item xs={12}>
                            <Select<IValue>
                              isClearable
                              options={companyWebsites}
                              label={t('company__type__', {
                                type: t('website'),
                              })}
                              placeholder={t('company__type__', {
                                type: t('website'),
                              })}
                              fullWidth
                              menuPosition="fixed"
                              value={values.codeDestinationCompanyWebsite}
                              onChange={(option) =>
                                setValue(
                                  'codeDestinationCompanyWebsite',
                                  option ? option.value : ''
                                )
                              }
                              error={Boolean(
                                errors.codeDestinationCompanyWebsite
                              )}
                              helperText={errors.codeDestinationCompanyWebsite}
                            />
                          </Grid>
                        </>
                      )}
                    </Grid>
                  )}
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
        <StickyFormFooter
          onCancel={() =>
            history.push(
              path('code.list', {
                companyID: company._id,
              })
            )
          }
          onSubmit={handleSubmit}
          isSubmitting={isUpdatingCode || isCreatingCode}
        />
      </Grid>
    </Paper>
  );
}

export default () => <CodeLayout main={<CodeDetail />} />;
